import React, { JSX, useEffect, useRef, useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import AuthRequired from './shared/authentication/request-authentication';
import Home from './pages/Home';
import About from './pages/About';
import Configuracion from './pages/Configuracion';
import KanbanBoard from './kanban/kanvan.page';
import { Inbox } from './inbox/Inbox';
import FileExplorer from './explorer/FileExplorer';
import ActividadesPage from './pages/actividades/actividades-page';
import ProcesosPage from './pages/procesos/procesos-page';
import ProcesosPeriodoPage from './pages/procesos/procesos-periodo-page';
import ProcesosPeriodoInstanciaPage from './pages/procesos/procesos-periodo-instancia-page';
import { requestNotificationPermission, onMessageListener } from './config/firebase';
import Manager from './manager/Manager';
import EmpresaTable from './manager/grupo/EmpresaTable';
import Grupo from './manager/grupo/Grupo';
import CompaniasLayout from './manager/proveedor/CompaniasLayout';
import ProveedorServiciosTable from './manager/proveedor/ProveedorServiciosTable';
import WorkflowPage from './pages/workflow/workflow-page';
import WorkflowClasesModal from './pages/workflow/workflow-clases-modal';
import WorkflowClaseNewModal from './pages/workflow/workflow-clase-new-modal';
import WorkflowClaseEditModal from './pages/workflow/workflow-clase-edit-modal';
import WorkflowNewModal from './pages/workflow/workflow-new-modal';
import WorkflowEditModal from './pages/workflow/workflow-edit-modal';
import WorkflowAppDisenoPage from './pages/workflow/workflow-diseno-page';
import WorkflowAppConfigPage from './pages/workflow/workflow-config-page';
import WorkflowAppEmpresaDisenoPage from './pages/workflow/workflow-empresa-diseno-page';
import { useAppDispatch, useAppSelector } from './store';
import { getSession, clearAuth } from './shared/authentication/reducers/authentication';
import { hasAnyAuthority, APLICACION_ACCEDER_REQUIRED_AUTHORITIES, BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS_REQUIRED_AUTHORITIES } from './shared/authorization';
import RequireAuthorities from './shared/RequireAuthorities';
import { FaInbox } from '@react-icons/all-files/fa/FaInbox';
import { FaColumns } from '@react-icons/all-files/fa/FaColumns';
import { FaFolder } from '@react-icons/all-files/fa/FaFolder';
import { FaClipboardList } from '@react-icons/all-files/fa/FaClipboardList';
import { FaStream } from '@react-icons/all-files/fa/FaStream';
import { FaUserShield } from '@react-icons/all-files/fa/FaUserShield';
import { FaConciergeBell } from '@react-icons/all-files/fa/FaConciergeBell';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaBell } from '@react-icons/all-files/fa/FaBell';
import { FaUserCircle } from '@react-icons/all-files/fa/FaUserCircle';
import { FaTachometerAlt } from '@react-icons/all-files/fa/FaTachometerAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaSignOutAlt } from '@react-icons/all-files/fa/FaSignOutAlt';
import { FaBars } from '@react-icons/all-files/fa/FaBars';
import { FaInfoCircle } from '@react-icons/all-files/fa/FaInfoCircle';
import { FaSearch } from '@react-icons/all-files/fa/FaSearch';
import { FaLock } from '@react-icons/all-files/fa/FaLock';
import ThemeToggle from './commons/ThemeToggle';
import AudioToggle from './commons/AudioToggle';
import AccountModal from './commons/AccountModal';

interface NavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  adminOnly?: boolean;
  /** Si está definido, el ítem solo se muestra con alguna de estas autoridades. */
  requiredAuthorities?: string[];
}

const navItems: NavItem[] = [
  { to: '/',           label: 'Dashboard',    icon: <FaTachometerAlt />, requiredAuthorities: APLICACION_ACCEDER_REQUIRED_AUTHORITIES },
  { to: '/inbox',      label: 'Inbox',        icon: <FaInbox /> },
  { to: '/mis-tareas', label: 'Mis Tareas', icon: <FaColumns /> },
  { to: '/explorer',   label: 'Drive',        icon: <FaFolder /> },
  { to: '/actividades',label: 'Actividades',  icon: <FaClipboardList /> },
  { to: '/procesos',   label: 'Procesos',     icon: <FaStream /> },
  { to: '/empresas', label: 'Empresas', icon: <FaUserShield />, requiredAuthorities: BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS_REQUIRED_AUTHORITIES },
  { to: '/companias', label: 'Compañías & Servicios', icon: <FaConciergeBell />, requiredAuthorities: BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS_REQUIRED_AUTHORITIES },
  { to: '/workflow', label: 'Workflow', icon: <FaProjectDiagram /> },
  { to: '/configuracion', label: 'Configuración', icon: <FaCog /> },
  { to: '/about',         label: 'Acerca',        icon: <FaInfoCircle /> },
];

function SidebarItem({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <Link to={item.to} className={`crm-nav-item${active ? ' active' : ''}`}>
      <span className="crm-nav-icon">{item.icon}</span>
      <span className="crm-nav-label">{item.label}</span>
    </Link>
  );
}

function UserAvatar({ userId, className, size = 24 }: { userId?: number | string; className?: string; size?: number }) {
  const [errored, setErrored] = useState(false);
  if (!errored && userId) {
    return (
      <img
        src={`/public/usuario/${userId}/image.png`}
        alt="avatar"
        className={className}
        width={size}
        height={size}
        onError={() => setErrored(true)}
        style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return <FaUserCircle className={className} style={{ fontSize: size, flexShrink: 0 }} />;
}

function UserMenu({
  userId,
  login,
  firstName,
  lastName,
  email,
  onLogout,
}: {
  userId?: number | string;
  login: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  onLogout: () => void;
}) {
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || login || 'Cuenta';
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="crm-user-pill"
        onClick={() => setOpen(v => !v)}
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <UserAvatar userId={userId} className="crm-user-avatar" size={40} />
        <span className="crm-user-name">{displayName}</span>
      </button>

      {open && (
        <div style={menuStyles.dropdown}>
          <button
            type="button"
            style={menuStyles.item}
            onClick={() => {
              setOpen(false);
              setAccountOpen(true);
            }}
          >
            <UserAvatar userId={userId} size={15} />
            Cuenta
          </button>
          <div style={menuStyles.separator} />
          <button style={{ ...menuStyles.item, ...menuStyles.logoutItem }} onClick={onLogout}>
            <FaSignOutAlt style={menuStyles.itemIcon} />
            Cerrar Sesión
          </button>
        </div>
      )}

      <AccountModal
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        userId={userId}
        login={login}
        firstName={firstName}
        lastName={lastName}
        email={email}
      />
    </div>
  );
}

const menuStyles: Record<string, React.CSSProperties> = {
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
    minWidth: 180,
    zIndex: 200,
    overflow: 'hidden',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 16px',
    fontSize: 14,
    fontWeight: 500,
    color: '#1e293b',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.12s',
  },
  logoutItem: {
    color: '#ef4444',
  },
  itemIcon: {
    fontSize: 15,
    flexShrink: 0,
  },
  separator: {
    height: 1,
    background: '#f1f5f9',
    margin: '2px 0',
  },
};

function extractForbiddenDetail(error: AxiosError): string {
  const data = error.response?.data;
  if (data == null) return error.message || 'Sin información adicional.';
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && data !== null) {
    const o = data as Record<string, unknown>;
    if ('detail' in o && o.detail != null) {
      if (typeof o.detail === 'string') return o.detail;
      try {
        return JSON.stringify(o.detail, null, 2);
      } catch {
        return String(o.detail);
      }
    }
    if (typeof o.message === 'string') return o.message;
    if (typeof o.title === 'string') return o.title;
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }
  return error.message || 'Sin información adicional.';
}

function isEmpresasGrupoEditPath(pathname: string): boolean {
  return /^\/empresas\/grupo\/[^/]+\/edit$/.test(pathname);
}

function isEmpresasGrupoEmpresaNewPath(pathname: string): boolean {
  return /^\/empresas\/grupo\/[^/]+\/empresa\/new$/.test(pathname);
}

function isEmpresasGrupoEmpresaEditPath(pathname: string): boolean {
  return /^\/empresas\/grupo\/[^/]+\/empresa\/[^/]+\/edit$/.test(pathname);
}

const forbiddenModalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100001,
    padding: 24,
  },
  dialog: {
    background: 'var(--crm-surface)',
    color: 'var(--crm-body-text)',
    borderRadius: 12,
    boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
    maxWidth: 480,
    width: '100%',
    maxHeight: 'min(80vh, 560px)',
    display: 'flex',
    flexDirection: 'column',
    padding: 24,
    gap: 12,
    border: '1px solid var(--crm-border)',
  },
  lockFigure: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 4,
  },
  lockCircle: {
    width: 112,
    height: 112,
    borderRadius: '50%',
    background: 'var(--crm-surface-muted)',
    border: '1px solid var(--crm-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.08)',
  },
  lockIcon: {
    width: 64,
    height: 64,
    color: 'var(--crm-text-secondary)',
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--crm-text-primary)',
    lineHeight: 1.35,
  },
  detailLabel: {
    margin: 0,
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--crm-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  detail: {
    margin: 0,
    padding: 12,
    borderRadius: 8,
    background: 'var(--crm-surface-muted)',
    border: '1px solid var(--crm-border)',
    fontSize: 13,
    lineHeight: 1.45,
    color: 'var(--crm-text-primary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflow: 'auto',
    flex: '1 1 auto',
    minHeight: 0,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    paddingTop: 4,
    flexShrink: 0,
  },
  acceptBtn: {
    padding: '10px 18px',
    borderRadius: 8,
    border: 'none',
    background: '#3b82f6',
    color: '#fff',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
};

function Main() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notification, setNotification] = useState<any>(null);
  const [token, setToken] = useState<string | null>(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);
  const [permissionDeniedDetail, setPermissionDeniedDetail] = useState<string | null>(null);
  const interceptorRef = useRef<number | null>(null);

  const location = useLocation();
  const locationPathRef = useRef(location.pathname);
  locationPathRef.current = location.pathname;
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const sessionHasBeenFetched = useAppSelector(state => state.authentication.sessionHasBeenFetched);
  const sessionLoading = useAppSelector(state => state.authentication.loading);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, ['ROLE_ADMIN']));
  const account = useAppSelector(state => state.authentication.account);

  // Global axios interceptor: 401 → clear session (AuthRequired); 403 → modal (still logged in)
  useEffect(() => {
    interceptorRef.current = axios.interceptors.response.use(
      response => response,
      error => {
        const status = error?.response?.status;
        if (status === 401) {
          dispatch(clearAuth());
        } else if (status === 403 && axios.isAxiosError(error)) {
          setPermissionDeniedDetail(extractForbiddenDetail(error));
          const p = locationPathRef.current;
          if (p === '/empresas/grupo/new') {
            window.dispatchEvent(new CustomEvent('profase:suppress-grupo-crear-for-403'));
          } else if (isEmpresasGrupoEditPath(p)) {
            window.dispatchEvent(new CustomEvent('profase:suppress-grupo-editar-for-403'));
          } else if (isEmpresasGrupoEmpresaNewPath(p)) {
            window.dispatchEvent(new CustomEvent('profase:suppress-empresa-crear-for-403'));
          } else if (isEmpresasGrupoEmpresaEditPath(p)) {
            window.dispatchEvent(new CustomEvent('profase:suppress-empresa-editar-for-403'));
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [dispatch]);

  // Fetch session on mount
  useEffect(() => { dispatch(getSession()); }, []);

  // No auto-redirect — show the login page instead (handled below)

  useEffect(() => {
    const unsubscribe = onMessageListener((payload) => {
      setNotification(payload);
      if (Notification.permission === 'granted') {
        new Notification(payload.notification?.title || 'Notificación', {
          body: payload.notification?.body,
          icon: payload.notification?.icon || '/logo192.png',
        });
      }
    });
    return () => {};
  }, []);

  const handleEnableNotifications = async () => {
    setNotifLoading(true);
    setNotifError(null);
    const result = await requestNotificationPermission();
    if (result.success && result.token) {
      setToken(result.token);
    } else {
      setNotifError(result.error || 'Error desconocido');
      if (result.errorType === 'brave-blocked') alert(result.error);
    }
    setNotifLoading(false);
  };

  useEffect(() => { handleEnableNotifications(); }, []);

  const closeForbiddenModal = () => {
    setPermissionDeniedDetail(null);
    window.dispatchEvent(new CustomEvent('profase:forbidden-modal-closed'));
  };
  const forbiddenModal =
    permissionDeniedDetail !== null ? (
      <div className="profase-modal-backdrop" style={forbiddenModalStyles.overlay} role="presentation" onClick={closeForbiddenModal}>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="forbidden-title"
          className="profase-modal-dialog"
          style={{ ...forbiddenModalStyles.dialog, maxHeight: 'min(80vh, 560px)', borderRadius: 12 }}
          onClick={e => e.stopPropagation()}
        >
          <figure style={forbiddenModalStyles.lockFigure} aria-hidden>
            <span style={forbiddenModalStyles.lockCircle}>
              <FaLock style={forbiddenModalStyles.lockIcon} />
            </span>
          </figure>
          <h2 id="forbidden-title" style={forbiddenModalStyles.title}>
            El usuario no tiene los permisos necesarios.
          </h2>
          <p style={forbiddenModalStyles.detailLabel}>Detalle del error</p>
          <pre style={forbiddenModalStyles.detail}>{permissionDeniedDetail}</pre>
          <div style={forbiddenModalStyles.actions}>
            <button type="button" style={forbiddenModalStyles.acceptBtn} onClick={closeForbiddenModal}>
              Aceptar
            </button>
          </div>
        </div>
      </div>
    ) : null;

  // Show full-screen spinner while GET /api/account is in-flight
  if (!sessionHasBeenFetched) {
    return (
      <>
        {forbiddenModal}
        <div style={loadingStyles.shell}>
          <div style={loadingStyles.spinner} />
          <p style={loadingStyles.text}>Verificando sesión…</p>
        </div>
      </>
    );
  }

  // Session fetched but user is not authenticated → show the login page
  if (sessionHasBeenFetched && !isAuthenticated) {
    return (
      <>
        {forbiddenModal}
        <AuthRequired />
      </>
    );
  }

  // Still waiting for the session check (shouldn't reach here, spinner above handles it)
  if (!isAuthenticated) return null;

  const visibleNavItems = navItems.filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.requiredAuthorities?.length) {
      if (!hasAnyAuthority(account.authorities, item.requiredAuthorities)) return false;
    }
    return true;
  });
  const currentLabel = visibleNavItems.find(item =>
    item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)
  )?.label ?? 'Inicio';

  return (
    <div className="crm-shell">
      {forbiddenModal}
      {/* ── Sidebar ── */}
      <aside className={`crm-sidebar${sidebarCollapsed ? ' collapsed' : ''}`}>
        <div className="crm-sidebar-header">
          <img src="/logo.png" alt="Logo" width="32" height="32" className="crm-logo-img" />
          {!sidebarCollapsed && <span className="crm-brand">Profase CRM</span>}
        </div>

        <nav className="crm-nav">
          {visibleNavItems.map(item => (
            <SidebarItem
              key={item.to}
              item={item}
              active={item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)}
            />
          ))}
        </nav>

        <div className="crm-sidebar-footer">
          {isAdmin && (
            <a href="/admin" className="crm-nav-item">
              <span className="crm-nav-icon"><FaCog /></span>
              {!sidebarCollapsed && <span className="crm-nav-label">Admin</span>}
            </a>
          )}
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="crm-main">
        {/* Top Header */}
        <header className="crm-topbar">
          <div className="crm-topbar-left">
            <button className="crm-toggle-btn" onClick={() => setSidebarCollapsed(v => !v)} title="Toggle sidebar">
              <FaBars />
            </button>
            <span className="crm-page-title">{currentLabel}</span>
          </div>

          <div className="crm-topbar-center">
            <div className="crm-search-box">
              <FaSearch className="crm-search-icon" />
              <input type="text" placeholder="Buscar en CRM..." className="crm-search-input" />
            </div>
          </div>

          <div className="crm-topbar-right">
            <ThemeToggle />
            <AudioToggle />
            <button className="crm-icon-btn" title="Notificaciones">
              <FaBell />
              {notification && <span className="crm-badge" />}
            </button>
            <UserMenu
              userId={account?.id}
              login={account?.login ?? ''}
              firstName={account?.firstName}
              lastName={account?.lastName}
              email={account?.email}
              onLogout={async () => {
                try { await axios.get('/auth/logout'); } catch (_) {}
                dispatch(clearAuth());
              }}
            />
          </div>
        </header>

        {/* Page content */}
        <main className="crm-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/mis-tareas" element={<KanbanBoard />} />
            <Route path="/explorer/*" element={<FileExplorer />} />
            <Route path="/actividades" element={<ActividadesPage />} />
            <Route path="/procesos" element={<ProcesosPage />}>
              <Route path="periodo" element={<ProcesosPeriodoPage />} />
              <Route path="periodo-actividad-instancia" element={<ProcesosPeriodoInstanciaPage />} />
            </Route>
            <Route
              path="/empresas"
              element={
                <RequireAuthorities
                  requiredAuthorities={BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS_REQUIRED_AUTHORITIES}
                  backTo="/"
                  backLabel="Ir al inicio"
                >
                  <Manager />
                </RequireAuthorities>
              }
            >
              <Route index element={<EmpresaTable />} />
              <Route path="grupo/new" element={<EmpresaTable />} />
              <Route path="grupo/:grupoId/empresa/:empresaId/edit" element={<Grupo />} />
              <Route path="grupo/:grupoId/empresa/:empresaId" element={<Grupo />} />
              <Route path="grupo/:grupoId/edit" element={<Grupo />} />
              <Route path="grupo/:grupoId" element={<Grupo />} />
            </Route>
            <Route
              path="/companias"
              element={
                <RequireAuthorities
                  requiredAuthorities={BASE_DE_CLIENTES_VER_DATOS_DE_EMPRESAS_REQUIRED_AUTHORITIES}
                  backTo="/"
                  backLabel="Ir al inicio"
                >
                  <CompaniasLayout />
                </RequireAuthorities>
              }
            >
              <Route index element={<ProveedorServiciosTable />} />
              <Route path=":id" element={<ProveedorServiciosTable />} />
            </Route>
            <Route path="/workflow" element={<WorkflowPage />}>
              <Route path="new" element={<WorkflowNewModal />} />
              <Route path="clases/new" element={<WorkflowClaseNewModal />} />
              <Route path="clases/:id" element={<WorkflowClaseEditModal />} />
              <Route path="clases" element={<WorkflowClasesModal />} />
              <Route path=":id/diseno" element={<WorkflowAppDisenoPage />} />
              <Route path=":id/workflow-empresa/:workflowEmpresaId/diseno" element={<WorkflowAppEmpresaDisenoPage />} />
              <Route path=":id/config" element={<WorkflowAppConfigPage />} />
              <Route path=":id" element={<WorkflowEditModal />} />
            </Route>
            <Route path="/configuracion/*" element={<Configuracion />} />
            <Route path="/about" element={<About />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default Main;

const loadingStyles: Record<string, React.CSSProperties> = {
  shell: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f2f5',
    gap: 16,
  },
  spinner: {
    width: 40,
    height: 40,
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  text: {
    margin: 0,
    fontSize: 14,
    color: '#64748b',
  },
};
